import * as React from "react";
import { css } from "@emotion/core";
import {
  FormGroup,
  InputGroup,
  TextArea,
  Button,
  Callout,
} from "@blueprintjs/core";
import {
  DateInput,
  TimePicker,
  TimezoneDisplayFormat,
  TimezoneSelect,
} from "@blueprintjs/datetime";
import { OrderMachineContext } from "../state/orderMachine";
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import { validateTimeRange } from "../utils/validation";

export default function ReviewForm(props: {
  mapRef: React.RefObject<mapboxgl.Map>;
}) {
  const [isVisible, setIsVisible] = React.useState(false);
  const [shouldAnimate, setShouldAnimate] = React.useState(false);
  const [naturalHeight, setNaturalHeight] = React.useState<number | null>(null);

  const containerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (isVisible && containerRef.current && naturalHeight === null) {
      setNaturalHeight(containerRef.current.scrollHeight + 16);
    }
  }, [isVisible, naturalHeight]);

  const review =
    OrderMachineContext.useSelector((state) => {
      return state.context.timezone;
    }) !== null;

  React.useLayoutEffect(() => {
    if (review) {
      setIsVisible(true);
      setShouldAnimate(true);
    } else {
      setShouldAnimate(false);
      setIsVisible(false);
      setNaturalHeight(null);
    }
  }, [review]);

  React.useEffect(() => {
    if (props.mapRef && containerRef.current) {
      const container = containerRef.current;
      const handler = () => {
        if (props.mapRef.current) {
          props.mapRef.current.resize();
        }
      };

      container.addEventListener("transitionend", handler);

      return () => {
        container.removeEventListener("transitionend", handler);
      };
    }
  }, [shouldAnimate, props.mapRef]);

  React.useEffect(() => {
    const mapRef = props.mapRef;

    return () => {
      if (mapRef.current) {
        mapRef.current.resize();
      }
    };
  }, [props.mapRef]);

  const orderMachine = OrderMachineContext.useActorRef();

  const timezone = OrderMachineContext.useSelector(
    (state) => state.context.timezone
  );
  const latitude = OrderMachineContext.useSelector(
    (state) => state.context.center?.[1] ?? "0"
  );
  const longitude = OrderMachineContext.useSelector(
    (state) => state.context.center?.[0] ?? "0"
  );
  const startTime = OrderMachineContext.useSelector(
    (state) => state.context.startTime
  );
  const endTime = OrderMachineContext.useSelector(
    (state) => state.context.endTime
  );
  const lockDetails = OrderMachineContext.useSelector(
    (state) => state.context.lockDetails
  );

  const isValidLatitude = (lat: string) => {
    const num = parseFloat(lat);
    return !isNaN(num) && num >= -90 && num <= 90;
  };

  const isValidLongitude = (lon: string) => {
    const num = parseFloat(lon);
    return !isNaN(num) && num >= -180 && num <= 180;
  };

  const mergeDateAndTime = (
    dateValue: string,
    timeValue: Date | string | null | undefined
  ): Date => {
    const time =
      timeValue instanceof Date ? timeValue : new Date(timeValue || Date.now());

    const dateTimeString = `${dateValue}T${time
      .getHours()
      .toString()
      .padStart(2, "0")}:${time.getMinutes().toString().padStart(2, "0")}:${time
      .getSeconds()
      .toString()
      .padStart(2, "0")}.${time.getMilliseconds().toString().padStart(3, "0")}`;

    return zonedTimeToUtc(dateTimeString, timezone ?? "Etc/UTC");
  };

  const startTimeDate = startTime ? new Date(startTime) : undefined;
  const endTimeDate = endTime ? new Date(endTime) : undefined;

  return (
    <div
      ref={containerRef}
      css={css`
        width: 100%;
        margin-top: ${isVisible ? "1rem" : "0"};
        padding: ${isVisible ? "1rem" : "0"};
        height: 100%;
        display: flex;
        flex-direction: column;
        background-color: var(--neutral-6);
        border: 1px solid var(--neutral-5);
        border-radius: 0.25rem;
        max-height: ${shouldAnimate
          ? naturalHeight !== null
            ? `${naturalHeight}px`
            : "100%"
          : "0px"};
        opacity: ${shouldAnimate ? 1 : 0};
        overflow: hidden;
        transition: max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1),
          opacity 0.2s ease-in;
        transition-delay: ${shouldAnimate ? "0s, 0.1s" : "0s, 0s"};
      `}
    >
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
          height: 41px;
        `}
      >
        <h4
          css={css`
            margin: 0;
            padding: 0.25rem 0;
            color: var(--neutral-3);
          `}
        >
          Review Survey Details
        </h4>
        {lockDetails && (
          <Button
            icon={lockDetails ? "lock" : "unlock"}
            onClick={() => orderMachine.send({ type: "TOGGLE_DETAILS_LOCK" })}
            alignText="left"
          >
            Unlock to Modify
          </Button>
        )}
        {lockDetails === false &&
          !validateTimeRange(startTime, endTime).isValid && (
            <div>
              {(() => {
                const validation = validateTimeRange(startTime, endTime);
                switch (validation.errorType) {
                  case "missing":
                    return (
                      <Callout compact intent="danger" icon="error">
                        Start and end time are required
                      </Callout>
                    );
                  case "reversed":
                    return (
                      <Callout compact intent="danger" icon="error">
                        Start time must be before end time
                      </Callout>
                    );
                  case "same":
                    return (
                      <Callout compact intent="danger" icon="error">
                        Start and end time cannot be the same
                      </Callout>
                    );
                  default:
                    return null;
                }
              })()}
            </div>
          )}
      </div>
      <div
        css={css`
          display: flex;
          flex-direction: column;
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: row;
            gap: 1rem;
          `}
        >
          <div
            css={css`
              flex: 1;
            `}
          >
            <FormGroup label="Latitude" labelFor="latitude-input">
              <InputGroup
                id="latitude-input"
                placeholder="e.g., 40.7128"
                value={latitude}
                onChange={(e) => {
                  orderMachine.send({
                    type: "SET_CENTER",
                    latitude: e.target.value,
                  });
                  orderMachine.send({
                    type: "SET_CUSTOM_DETAILS",
                    customDetails: true,
                  });
                }}
                intent={
                  latitude && !isValidLatitude(latitude) ? "danger" : "none"
                }
                disabled={lockDetails}
              />
            </FormGroup>
          </div>
          <div
            css={css`
              flex: 1;
            `}
          >
            <FormGroup label="Longitude" labelFor="longitude-input">
              <InputGroup
                id="longitude-input"
                placeholder="e.g., -74.0060"
                value={longitude}
                onChange={(e) => {
                  orderMachine.send({
                    type: "SET_CENTER",
                    longitude: e.target.value,
                  });
                  orderMachine.send({
                    type: "SET_CUSTOM_DETAILS",
                    customDetails: true,
                  });
                }}
                intent={
                  longitude && !isValidLongitude(longitude) ? "danger" : "none"
                }
                disabled={lockDetails}
              />
            </FormGroup>
          </div>
        </div>
        <div
          css={css`
            grid-column: span 2;
            display: flex;
            gap: 3rem;
          `}
        >
          <div
            css={css`
              flex: 1;
              display: flex;
              flex-direction: row;
              align-items: top;
              gap: 1rem;
            `}
          >
            <div
              css={css`
                flex: 1;
              `}
            >
              <div
                css={css`
                  display: flex;
                  flex-direction: row;
                  gap: 0.25rem;
                `}
              >
                <div
                  css={css`
                    flex: 1;
                  `}
                >
                  <FormGroup label="Start Time" labelFor="start-date-input">
                    <div
                      css={css`
                        margin-top: 4px;
                        box-shadow: 0 0 4px 0
                          ${!validateTimeRange(startTime, endTime).isValid
                            ? "var(--danger)"
                            : "transparent"};
                      `}
                    >
                      <DateInput
                        key={
                          startTime === null
                            ? "start-date-input-empty"
                            : "start-date-input-filled"
                        }
                        invalidDateMessage="Start date is required"
                        value={
                          startTimeDate
                            ? startTimeDate.toISOString()
                            : undefined
                        }
                        onChange={(newDate) => {
                          const merged = mergeDateAndTime(
                            newDate,
                            startTimeDate
                          );
                          orderMachine.send({
                            type: "SET_START_TIME",
                            startTime: merged.toISOString(),
                          });
                          orderMachine.send({
                            type: "SET_CUSTOM_DETAILS",
                            customDetails: true,
                          });
                        }}
                        placeholder="Select start date"
                        dateFnsFormat="LLL do, yyyy"
                        timezone={timezone ?? "Etc/UTC"}
                        disabled={lockDetails}
                      />
                    </div>
                  </FormGroup>
                </div>
                <TimePicker
                  value={
                    timezone && startTimeDate
                      ? utcToZonedTime(startTimeDate.toISOString(), timezone)
                      : startTimeDate
                  }
                  onChange={(newTime) => {
                    const dateString = startTimeDate
                      ? startTimeDate.toISOString().split("T")[0]
                      : new Date().toISOString().split("T")[0];
                    const merged = mergeDateAndTime(dateString, newTime);
                    orderMachine.send({
                      type: "SET_START_TIME",
                      startTime: merged.toISOString(),
                    });
                    orderMachine.send({
                      type: "SET_CUSTOM_DETAILS",
                      customDetails: true,
                    });
                  }}
                  showArrowButtons
                  useAmPm
                  disabled={lockDetails}
                />
              </div>
            </div>
            <div
              css={css`
                flex: 1;
              `}
            >
              <div
                css={css`
                  display: flex;
                  flex-direction: row;
                  align-items: top;
                  gap: 0.25rem;
                `}
              >
                <div
                  css={css`
                    flex: 1;
                  `}
                >
                  <FormGroup label="End Time" labelFor="end-date-input">
                    <div
                      css={css`
                        margin-top: 4px;
                        box-shadow: 0 0 4px 0
                          ${!validateTimeRange(startTime, endTime).isValid
                            ? "var(--danger)"
                            : "transparent"};
                      `}
                    >
                      <DateInput
                        key={
                          endTime === null
                            ? "end-date-input-empty"
                            : "end-date-input-filled"
                        }
                        value={
                          endTimeDate ? endTimeDate.toISOString() : undefined
                        }
                        onChange={(newDate) => {
                          const merged = mergeDateAndTime(newDate, endTimeDate);
                          orderMachine.send({
                            type: "SET_END_TIME",
                            endTime: merged.toISOString(),
                          });
                          orderMachine.send({
                            type: "SET_CUSTOM_DETAILS",
                            customDetails: true,
                          });
                        }}
                        placeholder="Select end date"
                        dateFnsFormat="LLL do, yyyy"
                        timezone={timezone ?? "Etc/UTC"}
                        disabled={lockDetails}
                      />
                    </div>
                  </FormGroup>
                </div>
                <TimePicker
                  value={
                    timezone && endTimeDate
                      ? utcToZonedTime(endTimeDate, timezone)
                      : endTimeDate
                  }
                  onChange={(newTime) => {
                    const dateString = endTimeDate
                      ? endTimeDate.toISOString().split("T")[0]
                      : new Date().toISOString().split("T")[0];
                    const merged = mergeDateAndTime(dateString, newTime);
                    orderMachine.send({
                      type: "SET_END_TIME",
                      endTime: merged.toISOString(),
                    });
                    orderMachine.send({
                      type: "SET_CUSTOM_DETAILS",
                      customDetails: true,
                    });
                  }}
                  showArrowButtons
                  useAmPm
                  disabled={lockDetails}
                />
              </div>
            </div>
            <FormGroup label="Timezone" labelFor="timezone-input">
              <div
                css={css`
                  margin-top: 4px;
                `}
              >
                <TimezoneSelect
                  valueDisplayFormat={TimezoneDisplayFormat.ABBREVIATION}
                  value={timezone ?? "Etc/UTC"}
                  onChange={(tz) =>
                    orderMachine.send({
                      type: "SET_TIMEZONE",
                      timezone: tz,
                    })
                  }
                  disabled={lockDetails}
                />
              </div>
            </FormGroup>
          </div>
        </div>
      </div>
    </div>
  );
}
